import { Size, select } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import { type TableColumnInstance, type TableSortRule, type Tournament } from '@swf/poker';

import theme from '../../../themes';
import Buttons from '../../Sidecar/TournamentInfo/Buttons/Buttons';
import Table from '../../Table/Table';
import {
  CELL_BUY_IN,
  CELL_PLAYERS,
  CELL_PRIZE_POOL,
  CELL_START_STATE
} from '../../Table/columnsConfig';
import BaseHeader from '../BaseHeader/BaseHeader';

import {
  TXTCLI_N0_satellites_to_this_tourname_ELL,
  TXTCLI_Satellites_To_This_Tournament
} from './Satellites.messages.json';
import styles from './Satellites.scss';

const tableColumns = [
  { ...CELL_START_STATE, width: 120 },
  { ...CELL_BUY_IN, width: 80 },
  { ...CELL_PRIZE_POOL, width: 100 },
  { ...CELL_PLAYERS, width: 40 }
];

interface Props {
  satellites: Tournament[];
}

const Satellites: React.FC<Props> = ({ satellites }) => {
  const { setStyle } = useContext<ModalContextValue>(ModalContext);

  useEffect(() => {
    setStyle({
      closeOnOverlayClick: true,
      closeOnEsc: true,
      overlayClassName: classNames(theme.darkMode, styles.modalOverlay),
      className: classNames(styles.modalContainer, styles.modalContainerCentered),
      size: Size.MEDIUM
    });
  }, [setStyle]);

  const [selectedTournament, setSelectedTournament] = useState<Tournament | undefined>(
    satellites?.[0]
  );
  const [sortRule, setSortRule] = useState<TableSortRule>({ id: 'startTime', desc: false });
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);

  const handleColumnClick = (column: TableColumnInstance) =>
    setSortRule({ id: column.id, desc: !column.isSortedDesc });

  const handleRowClick = (tournamentId: number) => {
    const tournament = satellites.find((t) => t.id === tournamentId);
    setSelectedTournament(tournament);
  };

  const handleRowDoubleClick = (tournamentId: number) => {
    if (!isDesktopEmbedded) return;
    window.starsweb?.app?.execute(`tournament/${tournamentId}`);
  };

  return (
    <>
      <BaseHeader
        headerClassName={styles.header}
        title={<Translation {...TXTCLI_Satellites_To_This_Tournament} />}
      />
      <Body className={styles.body}>
        <Translation
          tagName="div"
          {...TXTCLI_N0_satellites_to_this_tourname_ELL}
          values={{ 0: satellites.length }}
        />
        <Table
          data={satellites}
          loading={false}
          selectedRowId={selectedTournament?.id || 0}
          sortRule={sortRule}
          handleColumnClick={handleColumnClick}
          handleRowClick={handleRowClick}
          handleRowDoubleClick={handleRowDoubleClick}
          columns={tableColumns}
        />
        {selectedTournament && (
          <div className={styles.buttons}>
            <Buttons tournament={selectedTournament} showLobbyButton={isDesktopEmbedded} />
          </div>
        )}
      </Body>
    </>
  );
};

export default Satellites;
